import { useState, useEffect, useRef, useCallback } from 'react';
import { MediaDevice } from '../types/interviews';
import { useToast } from '../components/common/Toast';

interface UseMediaDevicesProps {
  initialCameraEnabled?: boolean;
  initialMicEnabled?: boolean;
  onError?: (error: Error) => void;
}

interface UseMediaDevicesReturn {
  videoDevices: MediaDevice[];
  audioDevices: MediaDevice[];
  selectedVideoDevice: string;
  selectedAudioDevice: string;
  setSelectedVideoDevice: (deviceId: string) => void;
  setSelectedAudioDevice: (deviceId: string) => void;
  stream: MediaStream | null;
  videoRef: React.RefObject<HTMLVideoElement>;
  isCameraEnabled: boolean;
  isMicEnabled: boolean;
  hasCameraPermission: boolean | null;
  hasMicPermission: boolean | null;
  lastAudioConstraints: MediaTrackConstraints | null;
  toggleCamera: () => void;
  toggleMicrophone: () => void;
  handleDeviceChange: (deviceId: string, type: 'video' | 'audio') => Promise<void>;
  cleanupMediaTracks: () => Promise<void>;
  requestMediaAccess: () => Promise<MediaStream | null>;
}

interface InitMediaOptions {
  enableCamera?: boolean;
  enableMic?: boolean;
  cameraDeviceId?: string;
  micDeviceId?: string;
  retryCount?: number;
}

export const useMediaDevices = ({
  initialCameraEnabled = true,
  initialMicEnabled = true,
  onError
}: UseMediaDevicesProps = {}): UseMediaDevicesReturn => {
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const audioTrackRef = useRef<MediaStreamTrack | null>(null);
  const isChangingDevice = useRef(false);

  // Media state
  const [videoDevices, setVideoDevices] = useState<MediaDevice[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDevice[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
  const [isCameraEnabled, setIsCameraEnabled] = useState<boolean>(initialCameraEnabled);
  const [isMicEnabled, setIsMicEnabled] = useState<boolean>(initialMicEnabled);
  const [hasCameraPermission, setHasCameraPermission] = useState<boolean | null>(null);
  const [hasMicPermission, setHasMicPermission] = useState<boolean | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [lastAudioConstraints, setLastAudioConstraints] = useState<MediaTrackConstraints | null>(null);
  const { showToast } = useToast();
  // Add missing state for error management
  const [isStreamInitialized, setIsStreamInitialized] = useState<boolean>(false);
  const [mediaRetryAttempts, setMediaRetryAttempts] = useState<number>(0);
  const MAX_RETRY_ATTEMPTS = 3;
  
  // Add device error state to align with InterviewPreviewContainer
  const [deviceError, setDeviceError] = useState<{
    hasError: boolean;
    message: string;
    isBlocking: boolean;
  }>({
    hasError: false,
    message: '',
    isBlocking: false
  });
  
  // Add a flag to track if the hook is ready for video operations
  const [isVideoElementReady, setIsVideoElementReady] = useState<boolean>(false);
  const videoElementCheckAttempts = useRef<number>(0);
  
  // Use a ref to track the last time we initialized media
  const lastMediaInitTime = useRef<number>(0);
  
  // Define the initializeMedia function that was missing
  const initializeMedia = useCallback(async (options: InitMediaOptions = {}) => {
    console.log('[InitMedia] Starting media initialization with options:', options);
    const {
      enableCamera = isCameraEnabled,
      enableMic = isMicEnabled,
      cameraDeviceId = selectedVideoDevice,
      micDeviceId = selectedAudioDevice,
      retryCount = 0
    } = options;
    
    // If camera is enabled but video element isn't ready, warn about it
    if (enableCamera && !isVideoElementReady) {
      console.warn('[InitMedia] Video element not ready yet, but camera access requested.');
    }
    
    let constraints: MediaStreamConstraints = {};
    
    // Only request devices that are enabled
    if (enableCamera) {
      constraints.video = cameraDeviceId ? { deviceId: { exact: cameraDeviceId } } : true;
    }
    
    if (enableMic) {
      constraints.audio = micDeviceId ? { deviceId: { exact: micDeviceId } } : true;
    }
    
    // Don't proceed if we don't need any devices
    if (!constraints.video && !constraints.audio) {
      console.log('[InitMedia] No devices requested, skipping initialization');
      return null;
    }
    
    console.log('[InitMedia] Requesting media with constraints:', constraints);
    
    try {
      // First release any existing stream to avoid conflicts
      if (stream) {
        // Stop all tracks
        stream.getTracks().forEach(track => {
          try {
            track.enabled = false;
            track.stop();
            console.log(`[releaseMediaStream] Released track: ${track.kind} (${track.id})`);
          } catch (e) {
            console.error('[releaseMediaStream] Error releasing track:', e);
          }
        });
        
        // Clear video element
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.load(); // Force resource release
        }
        
        // Clear stream reference
        setStream(null);
      }
      
      // Request media access
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('[InitMedia] Media access granted:', 
        `Video tracks: ${newStream.getVideoTracks().length}`,
        `Audio tracks: ${newStream.getAudioTracks().length}`);
      
      // Update permission states based on what was granted
      setHasCameraPermission(newStream.getVideoTracks().length > 0);
      setHasMicPermission(newStream.getAudioTracks().length > 0);
      
      // Store the stream
      setStream(newStream);
      
      // Set UI states based on what was granted
      if (newStream.getVideoTracks().length > 0) {
        setIsCameraEnabled(true);
      }
      
      if (newStream.getAudioTracks().length > 0) {
        setIsMicEnabled(true);
      }
      
      // Update the video element
      if (videoRef.current) {
        console.log('[InitMedia] Setting video element srcObject');
        videoRef.current.srcObject = newStream;
        
        // Mark video element as ready if we successfully accessed it
        if (!isVideoElementReady) {
          setIsVideoElementReady(true);
        }
        
        // Attempt to play the video
        try {
          await videoRef.current.play();
          console.log('[InitMedia] Video playback started successfully');
        } catch (playError) {
          console.warn('[InitMedia] Autoplay prevented, user interaction may be required:', playError);
        }
      }
      
      // Verify that the stream is active after a short delay
      setTimeout(() => {
        if (videoRef.current) {
          const hasSource = Boolean(videoRef.current.srcObject);
          console.log('[InitMedia] Checking video element after delay:',
            `srcObject exists: ${hasSource}`,
            `Video paused: ${videoRef.current.paused}`,
            `Video tracks: ${newStream.getVideoTracks().length}`,
            `VideoTracks enabled: ${newStream.getVideoTracks().some(t => t.enabled)}`);
          
          // If video element has no source but should have one, try to fix it
          if (!hasSource && enableCamera) {
            console.warn('[InitMedia] Video element missing srcObject, attempting to fix');
            videoRef.current.srcObject = newStream;
            videoRef.current.play().catch(e => console.warn('Delayed play failed:', e));
          }
        }
      }, 500);
      
      return newStream;
    } catch (error: any) {
      console.error('[InitMedia] Error initializing media:', error);
      
      // Update permission states based on error
      if (error.name === 'NotAllowedError') {
        // Permission denied
        if (constraints.video) setHasCameraPermission(false);
        if (constraints.audio) setHasMicPermission(false);
      } else if (error.name === 'NotFoundError') {
        // Device not found
        console.warn('[InitMedia] Media device not found');
      }
      
      // Update UI state to match reality
      if (constraints.video) setIsCameraEnabled(false);
      if (constraints.audio) setIsMicEnabled(false);
      
      // Try again if we haven't reached our retry limit
      if (retryCount < 2) {
        console.log(`[InitMedia] Retrying media initialization (attempt ${retryCount + 1}/2)...`);
        setTimeout(() => {
          initializeMedia({
            ...options,
            retryCount: retryCount + 1
          });
        }, 1000);
      }
      
      // Call onError callback if provided
      if (onError && error instanceof Error) {
        onError(error);
      }
      
      return null;
    }
  }, [
    isCameraEnabled, 
    isMicEnabled, 
    selectedVideoDevice, 
    selectedAudioDevice, 
    videoRef,
    stream,
    isVideoElementReady,
    setHasCameraPermission,
    setHasMicPermission,
    setIsCameraEnabled,
    setIsMicEnabled,
    setIsVideoElementReady,
    onError
  ]);
  
  // Add a central initialization guard function
  const safelyInitializeMedia = useCallback(async (options: InitMediaOptions = {}) => {
    const now = Date.now();
    const timeSinceLastInit = now - lastMediaInitTime.current;
    
    // If we've tried to initialize recently, throttle
    if (timeSinceLastInit < 2000) { // 2 second cooldown
      console.log(`[safelyInitializeMedia] Throttling media initialization (last attempt ${timeSinceLastInit}ms ago)`);
      return null;
    }
    
    // Update the timestamp
    lastMediaInitTime.current = now;
    
    // Call the actual initialization function
    try {
      return await initializeMedia(options);
    } catch (err) {
      console.error('[safelyInitializeMedia] Error initializing media:', err);
      return null;
    }
  }, [initializeMedia]);
  
  // Simplified video element check
  useEffect(() => {
    // If already confirmed ready, skip this
    if (isVideoElementReady) return;
    
    // If no videoRef provided, don't check
    if (!videoRef) {
      console.log('[VideoElementCheck] No videoRef provided to hook');
      return;
    }
    
    // Simple check with fewer attempts
    const maxAttempts = 5;
    
    const checkVideoElement = () => {
      if (videoRef.current) {
        console.log('[VideoElementCheck] Video element found');
        setIsVideoElementReady(true);
        
        // If there's a stream waiting, attach it immediately
        if (stream && !videoRef.current.srcObject) {
          console.log('[VideoElementCheck] Attaching waiting stream to video element');
          videoRef.current.srcObject = stream;
        }
        return;
      }
      
      // Increment and check against max attempts
      videoElementCheckAttempts.current += 1;
      
      if (videoElementCheckAttempts.current >= maxAttempts) {
        console.log('[VideoElementCheck] Video element not found after maximum attempts');
        // We'll try again when the component re-renders or stream changes
        return;
      }
      
      // Schedule another check with a fixed delay
      setTimeout(checkVideoElement, 200);
    };
    
    // Start checking
    checkVideoElement();
    
    // Clean up function
    return () => {
      videoElementCheckAttempts.current = 0;
    };
  }, [videoRef, isVideoElementReady, stream]);

  // Fetch available media devices
  const getMediaDevices = useCallback(async () => {
    try {
      // Try to get device info without requesting permissions first
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      // If we have labels, we already have permissions
      const hasLabels = devices.some(device => device.label);
      
      // Only request permissions if we don't have labels already
      if (!hasLabels) {
        try {
          // First try with just audio since that's required
          await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (audioError) {
          console.warn('Error requesting audio permissions:', audioError);
          
          // If that fails, try with just video
          try {
            await navigator.mediaDevices.getUserMedia({ video: true });
          } catch (videoError) {
            console.warn('Error requesting video permissions:', videoError);
            // Both failed, but we'll still enumerate devices to see what's available
          }
        }
        
        // Re-enumerate after permission requests
        const devicesAfterPermissions = await navigator.mediaDevices.enumerateDevices();
        processDevices(devicesAfterPermissions);
      } else {
        // Already have permissions, process devices
        processDevices(devices);
      }
    } catch (error) {
      console.error('Error fetching media devices:', error);
      // Call onError callback if provided
      if (onError && error instanceof Error) {
        onError(error);
      }
    }
  }, [selectedVideoDevice, selectedAudioDevice, onError]);
  
  // Helper to process device lists
  const processDevices = useCallback((devices: MediaDeviceInfo[]) => {
      const cameras = devices.filter(device => device.kind === 'videoinput')
        .map(device => ({
          deviceId: device.deviceId,
          kind: device.kind,
          label: device.label || `Camera ${device.deviceId.slice(0, 5)}...`
        }));
        
      const microphones = devices.filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          kind: device.kind,
          label: device.label || `Microphone ${device.deviceId.slice(0, 5)}...`
        }));
      
      setVideoDevices(cameras);
      setAudioDevices(microphones);
      
      // Set default selections if available
      if (cameras.length > 0 && !selectedVideoDevice) {
        setSelectedVideoDevice(cameras[0].deviceId);
      }
      
      if (microphones.length > 0 && !selectedAudioDevice) {
        setSelectedAudioDevice(microphones[0].deviceId);
    }
  }, [selectedVideoDevice, selectedAudioDevice]);

  // Enhanced cleanup function that is more aggressive about releasing resources
  const cleanupMediaTracks = useCallback(async () => {
    const cleanupId = Date.now().toString().slice(-6);
    console.log(`[${cleanupId}] Aggressive media cleanup - START`);
    
    // First clear the video element - this is CRUCIAL for browsers to release camera
    if (videoRef.current) {
      try {
        // This sequence seems to work best for resource release
        videoRef.current.pause();
        videoRef.current.srcObject = null;
        // Force a reload of the video element to help with resource release
        videoRef.current.load();
        console.log(`[${cleanupId}] Cleared video element source`);
      } catch (videoError) {
        console.error(`[${cleanupId}] Error clearing video source:`, videoError);
      }
    }
    
    // Store local copies of all tracks to stop
    let allTracks: MediaStreamTrack[] = [];
    
    // Grab all tracks from the stream
    if (stream) {
      try {
        allTracks = [...stream.getTracks()];
        console.log(`[${cleanupId}] Found ${allTracks.length} tracks to clean up`);
      } catch (error) {
        console.error(`[${cleanupId}] Error getting tracks:`, error);
      }
    }
    
    // Clear track references BEFORE stopping tracks
    videoTrackRef.current = null;
    audioTrackRef.current = null;
    console.log(`[${cleanupId}] Cleared track references`);
    
    // Release stream reference BEFORE stopping tracks
    setStream(null);
    console.log(`[${cleanupId}] Released stream reference`);
    
    // Now stop all tracks with a more reliable approach
    for (const track of allTracks) {
      try {
        // First disable
        track.enabled = false;
        // Then stop
        track.stop();
        console.log(`[${cleanupId}] Stopped ${track.kind} track: ${track.id}`);
        
        // If this is a video track, try an additional cleanup step
        if (track.kind === 'video') {
          // Some browsers need this explicit release
          const constraints = track.getConstraints();
          if (constraints && Object.keys(constraints).length > 0) {
            try {
              // Try to reset the camera by requesting it with minimal constraints
              // and immediately stopping it - this can help release stuck resources
              navigator.mediaDevices.getUserMedia({ video: { deviceId: constraints.deviceId } })
                .then(tempStream => {
                  tempStream.getTracks().forEach(t => t.stop());
                  console.log(`[${cleanupId}] Extra cleanup: Released camera with reset request`);
                })
                .catch(e => {
                  // Ignore errors in this extra step
                  console.log(`[${cleanupId}] Extra cleanup attempt failed (normal):`, e);
                });
            } catch (e) {
              // Ignore errors in this extra step
            }
          }
        }
      } catch (trackError) {
        console.error(`[${cleanupId}] Error stopping track:`, trackError);
      }
    }
    
    // Force browsers to refresh device state
    setTimeout(() => {
      navigator.mediaDevices.enumerateDevices()
        .then(() => {
          console.log(`[${cleanupId}] Refreshed device enumeration`);
        })
        .catch(err => {
          console.warn(`[${cleanupId}] Error refreshing device enumeration:`, err);
        });
    }, 100);
    
    // Add an extra step to help Chrome specifically release camera LED
    try {
      // This can help reset Chrome's internal state tracking for camera LED
      const emptyTrackSettings = { audio: false, video: false };
      navigator.mediaDevices.getUserMedia(emptyTrackSettings)
        .then(emptyStream => {
          // Immediately stop this stream - it's just to help reset browser state
          emptyStream.getTracks().forEach(t => t.stop());
          console.log(`[${cleanupId}] Extra Chrome cleanup: Released with empty request`);
        })
        .catch(() => {
          // Ignore errors, this is just an extra cleanup step
        });
    } catch (e) {
      // Ignore errors in this extra cleanup step
    }
    
    console.log(`[${cleanupId}] Aggressive media cleanup - COMPLETED`);
    
    // Return a promise that resolves after a delay
    return new Promise<void>(resolve => {
      setTimeout(() => {
        console.log(`[${cleanupId}] Cleanup delay completed`);
        resolve();
      }, 300);
    });
  }, [stream]);

  // Add throttling to prevent rapid media initialization
  const throttledInitializeMedia = useCallback(async (options: InitMediaOptions = {}) => {
    const now = Date.now();
    const timeSinceLastInit = now - lastMediaInitTime.current;
    
    // If we've tried to initialize recently, throttle
    if (timeSinceLastInit < 2000) { // 2 second cooldown
      console.log(`[InitMedia] Throttling initialization (last attempt ${timeSinceLastInit}ms ago)`);
      return null;
    }
    
    // Update the timestamp
    lastMediaInitTime.current = now;
    
    console.log('[InitMedia] Starting media initialization with options:', options);
    const {
      enableCamera = isCameraEnabled,
      enableMic = isMicEnabled,
      cameraDeviceId = selectedVideoDevice,
      micDeviceId = selectedAudioDevice,
      retryCount = 0
    } = options;
    
    // If camera is enabled but video element isn't ready, warn about it
    if (enableCamera && !isVideoElementReady) {
      console.warn('[InitMedia] Video element not ready yet, but camera access requested.');
    }
    
      let constraints: MediaStreamConstraints = {};
      
    // Only request devices that are enabled
    if (enableCamera) {
      constraints.video = cameraDeviceId ? { deviceId: { exact: cameraDeviceId } } : true;
    }
    
    if (enableMic) {
      constraints.audio = micDeviceId ? { deviceId: { exact: micDeviceId } } : true;
    }
    
    // Don't proceed if we don't need any devices
    if (!constraints.video && !constraints.audio) {
      console.log('[InitMedia] No devices requested, skipping initialization');
      return null;
    }
    
    console.log('[InitMedia] Requesting media with constraints:', constraints);
    
    try {
      // First release any existing stream to avoid conflicts
      if (stream) {
        // Stop all tracks
        stream.getTracks().forEach(track => {
          try {
            track.enabled = false;
            track.stop();
            console.log(`[releaseMediaStream] Released track: ${track.kind} (${track.id})`);
          } catch (e) {
            console.error('[releaseMediaStream] Error releasing track:', e);
          }
        });
        
        // Clear video element
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.load(); // Force resource release
        }
        
        // Clear stream reference
        setStream(null);
      }
      
      // Request media access
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('[InitMedia] Media access granted:', 
        `Video tracks: ${newStream.getVideoTracks().length}`,
        `Audio tracks: ${newStream.getAudioTracks().length}`);
      
      // Update permission states based on what was granted
      setHasCameraPermission(newStream.getVideoTracks().length > 0);
      setHasMicPermission(newStream.getAudioTracks().length > 0);
      
      // Store the stream
      setStream(newStream);
      
      // Set UI states based on what was granted
      if (newStream.getVideoTracks().length > 0) {
        setIsCameraEnabled(true);
      }
      
      if (newStream.getAudioTracks().length > 0) {
        setIsMicEnabled(true);
      }
      
      // Update the video element
      if (videoRef.current) {
        console.log('[InitMedia] Setting video element srcObject');
        videoRef.current.srcObject = newStream;
        
        // Mark video element as ready if we successfully accessed it
        if (!isVideoElementReady) {
        setIsVideoElementReady(true);
      }
      
        // Attempt to play the video
        try {
          await videoRef.current.play();
          console.log('[InitMedia] Video playback started successfully');
        } catch (playError) {
          console.warn('[InitMedia] Autoplay prevented, user interaction may be required:', playError);
        }
      } else {
        console.warn('[InitMedia] No video element reference available, setting up retry mechanism');
        
        // Similar retry mechanism as in requestMediaAccess
        let attachRetryCount = 0;
        const maxRetries = 20;
        
        const attachStreamRetry = () => {
          attachRetryCount++;
            
            if (videoRef.current) {
            console.log(`[InitMedia] Found video element on retry ${attachRetryCount}, attaching stream`);
            videoRef.current.srcObject = newStream;
            
            // Try to play
            videoRef.current.play()
              .then(() => console.log('[InitMedia] Successfully started playback after retry'))
              .catch(err => console.warn('[InitMedia] Error starting playback after retry:', err));
              
            // Mark as ready
            setIsVideoElementReady(true);
          } else if (attachRetryCount < maxRetries) {
            setTimeout(attachStreamRetry, 100);
          } else {
            console.warn(`[InitMedia] Gave up trying to attach stream after ${maxRetries} attempts`);
          }
        };
        
        attachStreamRetry();
      }
      
      // Verify that the stream is active after a short delay
      setTimeout(() => {
        if (videoRef.current) {
          const hasSource = Boolean(videoRef.current.srcObject);
          console.log('[InitMedia] Checking video element after delay:',
            `srcObject exists: ${hasSource}`,
            `Video paused: ${videoRef.current.paused}`,
            `Video tracks: ${newStream.getVideoTracks().length}`,
            `VideoTracks enabled: ${newStream.getVideoTracks().some(t => t.enabled)}`);
          
          // If video element has no source but should have one, try to fix it
          if (!hasSource && enableCamera) {
            console.warn('[InitMedia] Video element missing srcObject, attempting to fix');
            videoRef.current.srcObject = newStream;
            videoRef.current.play().catch(e => console.warn('Delayed play failed:', e));
          }
        }
      }, 500);
      
      return newStream;
    } catch (error: any) {
      console.error('[InitMedia] Error initializing media:', error);
      
      // Update permission states based on error
      if (error.name === 'NotAllowedError') {
        // Permission denied
        if (constraints.video) setHasCameraPermission(false);
        if (constraints.audio) setHasMicPermission(false);
      } else if (error.name === 'NotFoundError') {
        // Device not found
        console.warn('[InitMedia] Media device not found');
      }
      
      // Update UI state to match reality
      if (constraints.video) setIsCameraEnabled(false);
      if (constraints.audio) setIsMicEnabled(false);
      
      // Try again if we haven't reached our retry limit
      if (retryCount < 2) {
        console.log(`[InitMedia] Retrying media initialization (attempt ${retryCount + 1}/2)...`);
        setTimeout(() => {
          throttledInitializeMedia({
            ...options,
            retryCount: retryCount + 1
          });
        }, 1000);
      }
      
      // Call onError callback if provided
      if (onError && error instanceof Error) {
        onError(error);
      }
      
      return null;
    }
  }, [
    isCameraEnabled, 
    isMicEnabled, 
    selectedVideoDevice, 
    selectedAudioDevice, 
    videoRef,
    stream,
    isVideoElementReady,
    setHasCameraPermission,
    setHasMicPermission,
    setIsCameraEnabled,
    setIsMicEnabled,
    setIsVideoElementReady,
    onError
  ]);
  
  // Request media access with proper error handling
  const requestMediaAccess = useCallback(async () => {
    // Use the throttled initialization function
    return throttledInitializeMedia({
      enableCamera: isCameraEnabled,
      enableMic: isMicEnabled,
      cameraDeviceId: selectedVideoDevice,
      micDeviceId: selectedAudioDevice
    });
  }, [throttledInitializeMedia, isCameraEnabled, isMicEnabled, selectedVideoDevice, selectedAudioDevice]);

  // Handle device change
  const handleDeviceChange = useCallback(async (deviceId: string, type: 'video' | 'audio') => {
    const logPrefix = `[Device Change - ${type}]`;
    console.log(`${logPrefix} Selected: ${deviceId}`);
    
    // Check if this is actually a change
    if ((type === 'video' && selectedVideoDevice === deviceId) || 
        (type === 'audio' && selectedAudioDevice === deviceId)) {
      console.log(`${logPrefix} Device selection unchanged, skipping`);
      return;
    }
    
    // Prevent empty selections
    if (!deviceId) {
      console.log(`${logPrefix} Empty device ID, skipping selection`);
      return;
    }
    
    // Prevent selection if already changing devices
    if (isChangingDevice.current) {
      console.log(`${logPrefix} Device change already in progress, skipping this request`);
      return;
    }
    
    console.log(`${logPrefix} Updating state with new device...`);
    
    // Set flag to prevent concurrent changes
    isChangingDevice.current = true;
    
    // Update the state for the UI
    if (type === 'video') {
      setSelectedVideoDevice(deviceId);
    } else {
      setSelectedAudioDevice(deviceId);
    }
    
    try {
      // First cleanup existing media to prevent resource conflicts
      await cleanupMediaTracks();
      
      // Check video element readiness for video device changes
      if (type === 'video' && !isVideoElementReady) {
        console.warn(`${logPrefix} Video element not ready, may not display video correctly`);
      }
      
      // Build new constraints based on the selected device
      let constraints: MediaStreamConstraints = {};
      
      if (type === 'video') {
        // For video device change, keep existing audio if enabled
        constraints = {
          video: { deviceId: { exact: deviceId } },
          audio: isMicEnabled ? (selectedAudioDevice ? { deviceId: { exact: selectedAudioDevice } } : true) : false
        };
      } else {
        // For audio device change, keep existing video if enabled
        constraints = {
          audio: { deviceId: { exact: deviceId } },
          video: isCameraEnabled ? (selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : true) : false
        };
      }
      
      console.log(`${logPrefix} Requesting media with constraints:`, JSON.stringify(constraints));
      
      // Request the new media stream with selected device
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Store individual track references
      const videoTracks = newStream.getVideoTracks();
      const audioTracks = newStream.getAudioTracks();
      
      if (videoTracks.length > 0) {
        videoTrackRef.current = videoTracks[0];
        // Ensure video track keeps previously set enabled state
        videoTracks[0].enabled = isCameraEnabled;
      }
      
      if (audioTracks.length > 0) {
        audioTrackRef.current = audioTracks[0];
        // Ensure audio track keeps previously set enabled state
        audioTracks[0].enabled = isMicEnabled;
      }
      
      // Update the stream reference
      setStream(newStream);
      
      // Update the video element if we have video tracks and it's a video device change or if video is enabled
      if (videoTracks.length > 0 && (type === 'video' || isCameraEnabled)) {
        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
          console.log(`${logPrefix} Updated video element with new stream`);
          
          // Mark video element as ready if we successfully accessed it
          if (!isVideoElementReady) {
            setIsVideoElementReady(true);
          }
        } else {
          console.warn(`${logPrefix} Video element not available, setting up retry mechanism`);
          
          // Similar retry mechanism as in requestMediaAccess
          let retryCount = 0;
          const maxRetries = 20;
          
          const attachStreamRetry = () => {
            retryCount++;
            
            if (videoRef.current) {
              console.log(`${logPrefix} Found video element on retry ${retryCount}, attaching stream`);
              videoRef.current.srcObject = newStream;
              
              // Try to play
              videoRef.current.play()
                .then(() => console.log(`${logPrefix} Successfully started playback after device change`))
                .catch(err => console.warn(`${logPrefix} Error starting playback after device change:`, err));
                
              // Mark as ready
              setIsVideoElementReady(true);
            } else if (retryCount < maxRetries) {
              setTimeout(attachStreamRetry, 100);
            } else {
              console.warn(`${logPrefix} Gave up trying to attach stream after ${maxRetries} attempts`);
            }
          };
          
          attachStreamRetry();
        }
      }
      
      console.log(`${logPrefix} Device change completed successfully`);
    } catch (error) {
      console.error(`${logPrefix} Error switching device:`, error);
      
      // Try with fallback constraints if exact constraint failed
      if (error instanceof DOMException && error.name === 'OverconstrainedError') {
        try {
          console.log(`${logPrefix} Trying with fallback constraints...`);
          
          // Try with ideal instead of exact
          const fallbackConstraints: MediaStreamConstraints = {};
          
          if (type === 'video') {
            fallbackConstraints.video = { deviceId: { ideal: deviceId } };
            if (isMicEnabled) {
              fallbackConstraints.audio = selectedAudioDevice 
                ? { deviceId: { ideal: selectedAudioDevice } } 
                : true;
            }
          } else {
            fallbackConstraints.audio = { deviceId: { ideal: deviceId } };
            if (isCameraEnabled) {
              fallbackConstraints.video = selectedVideoDevice 
                ? { deviceId: { ideal: selectedVideoDevice } } 
                : true;
            }
          }
          
          const fallbackStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
          
          // Update our state
          setStream(fallbackStream);
          
          if (videoRef.current) {
            videoRef.current.srcObject = fallbackStream;
          }
          
          console.log(`${logPrefix} Fallback device change successful`);
        } catch (fallbackError) {
          console.error(`${logPrefix} Fallback attempt also failed:`, fallbackError);
        }
      }
    } finally {
      // Reset the flag regardless of success or failure
      isChangingDevice.current = false;
    }
  }, [selectedVideoDevice, selectedAudioDevice, isCameraEnabled, isMicEnabled, cleanupMediaTracks, isVideoElementReady]);

  // Toggle camera on/off
  const toggleCamera = useCallback(() => {
    const newState = !isCameraEnabled;
    console.log(`[Camera Toggle] Toggling camera to ${newState ? 'ON' : 'OFF'}`);
    
    if (!newState) {
      // Turning camera OFF - fully release resources
      console.log('[Camera Toggle] Stopping camera and releasing resources');
      
      if (stream) {
        // First get all video tracks
        const videoTracks = stream.getVideoTracks();
        
        // Disable and stop all video tracks
        videoTracks.forEach(track => {
          try {
            // First disable the track
            track.enabled = false;
            
            // Capture a reference to the track before stopping (for debugging)
            const trackId = track.id;
            const trackLabel = track.label;
            
            // Then stop it
            track.stop();
            console.log(`[Camera Toggle] Successfully stopped video track: ${trackId} (${trackLabel})`);
            
            // Clear the video track reference
            if (videoTrackRef.current && videoTrackRef.current.id === trackId) {
              videoTrackRef.current = null;
            }
          } catch (e) {
            console.error('[Camera Toggle] Error stopping track:', e);
          }
        });
        
        // If we only have audio tracks left, create a new stream with just those tracks
        if (isMicEnabled && stream.getAudioTracks().length > 0) {
          // Create a new stream with only the audio tracks
          const audioOnlyStream = new MediaStream();
          stream.getAudioTracks().forEach(audioTrack => {
            audioOnlyStream.addTrack(audioTrack);
          });
          
          // Set the new audio-only stream
          setStream(audioOnlyStream);
          
          // Update the video element with the audio-only stream
          if (videoRef.current) {
            try {
              videoRef.current.srcObject = audioOnlyStream;
            } catch (e) {
              console.error('[Camera Toggle] Error updating video element with audio-only stream:', e);
            }
          }
          
          console.log('[Camera Toggle] Created new audio-only stream to preserve microphone');
        } else {
          // No audio tracks either, clear video element completely
          if (videoRef.current) {
            videoRef.current.srcObject = null;
            videoRef.current.load(); // Force resource release
          }
          
          // Also clear the stream if no audio tracks are left
          if (!isMicEnabled) {
            setStream(null);
          }
        }
        
        // Force browsers to refresh device state
        setTimeout(() => {
          navigator.mediaDevices.enumerateDevices()
            .catch(err => console.warn('[Camera Toggle] Error refreshing device enumeration:', err));
        }, 100);
      }
      
      // Update UI state
      setIsCameraEnabled(false);
    } else {
      // Turning camera ON - first check permission status
      console.log('[Camera Toggle] Turning camera ON - checking permissions');
      
      // Function to request camera access
      const requestCameraAccess = () => {
        // Build constraints - preserve audio if it's enabled
        const constraints: MediaStreamConstraints = {
          video: selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : true,
        };
        
        // If mic is enabled, include audio constraints to preserve microphone state
        if (isMicEnabled) {
          constraints.audio = selectedAudioDevice ? { deviceId: { exact: selectedAudioDevice } } : true;
        }
        
        // Request media with a retryable approach
        const attemptMediaRequest = (retryCount = 0) => {
          console.log(`[Camera Toggle] Requesting camera access (attempt ${retryCount + 1}) with constraints:`, constraints);
          
          navigator.mediaDevices.getUserMedia(constraints)
            .then(newStream => {
              const newVideoTracks = newStream.getVideoTracks();
              const newAudioTracks = newStream.getAudioTracks();
              
              console.log(`[Camera Toggle] Obtained new stream with ${newVideoTracks.length} video tracks and ${newAudioTracks.length} audio tracks`);
              
              // If we only requested video but have existing audio tracks to preserve
              if (!constraints.audio && stream && stream.getAudioTracks().length > 0) {
                console.log('[Camera Toggle] Preserving existing audio tracks');
                // Get existing audio tracks to preserve
                const existingAudioTracks = stream.getAudioTracks();
                
                // Add existing audio tracks to the new stream
                existingAudioTracks.forEach(audioTrack => {
                  if (audioTrack.readyState === 'live') {
                    try {
                      newStream.addTrack(audioTrack);
                      console.log(`[Camera Toggle] Added existing audio track ${audioTrack.id} to new stream`);
                    } catch (e) {
                      console.error('[Camera Toggle] Error adding existing audio track to new stream:', e);
                    }
                  }
                });
              }
              
              // Store the video track reference if we got one
              if (newVideoTracks.length > 0) {
                videoTrackRef.current = newVideoTracks[0];
                console.log(`[Camera Toggle] New video track: ${videoTrackRef.current.id} (${videoTrackRef.current.label})`);
              }
              
              // Store the audio track reference if we got a new one
              if (newAudioTracks.length > 0 && (!audioTrackRef.current || audioTrackRef.current.readyState !== 'live')) {
                audioTrackRef.current = newAudioTracks[0];
                console.log(`[Camera Toggle] New audio track: ${audioTrackRef.current.id} (${audioTrackRef.current.label})`);
              }
              
              // Update stream reference
              setStream(newStream);
              
              // Update video element
              if (videoRef.current) {
                videoRef.current.srcObject = newStream;
                videoRef.current.play().catch(err => {
                  console.warn('[Camera Toggle] Autoplay prevented:', err);
                });
              }
              
              // Log the final stream state
              console.log(`[Camera Toggle] Stream setup complete with ${newStream.getVideoTracks().length} video tracks and ${newStream.getAudioTracks().length} audio tracks`);
              
              // Update permission states
              setHasCameraPermission(true);
              if (newAudioTracks.length > 0) {
                setHasMicPermission(true);
              }
              
              // Update UI state
              setIsCameraEnabled(true);
            })
            .catch(error => {
              console.error('[Camera Toggle] Error accessing camera:', error);
              
              if (error.name === 'NotAllowedError') {
                showToast('Camera permission denied. Please enable it in browser settings manually.', 'error');
                return;
              }
              // Retry logic for transient errors (up to 2 retries)
              if (retryCount < 2 && 
                  (error.name === 'NotReadableError' || error.name === 'AbortError')) {
                console.log(`[Camera Toggle] Retrying camera access after error: ${error.name}`);
                setTimeout(() => attemptMediaRequest(retryCount + 1), 500);
                return;
              }
              
              // Keep the UI state in sync with reality
              setIsCameraEnabled(false);
              
              // If this was a permission denial, update the permission state
              if (error.name === 'NotAllowedError') {
                setHasCameraPermission(false);
              }
            });
        };
        
        // Start the media request process
        attemptMediaRequest();
      };
      
      // Check if we need to request permission
      if (hasCameraPermission === false) {
        // Permission was explicitly denied before, show a message to the user
        console.log('[Camera Toggle] Camera permission was previously denied. User needs to enable it in browser settings.');
        
        // We'll still try to request it in case the user changed settings
        requestCameraAccess();
      } else if (hasCameraPermission === null) {
        // We don't know the permission status yet, so request it
        console.log('[Camera Toggle] Camera permission status unknown, requesting permission');
        requestCameraAccess();
      } else {
        // We already have permission, proceed with enabling
        console.log('[Camera Toggle] Camera permission already granted, enabling camera');
        requestCameraAccess();
      }
    }
  }, [stream, videoRef, isCameraEnabled, isMicEnabled, selectedVideoDevice, selectedAudioDevice, hasCameraPermission]);
  
  // Toggle microphone on/off
  const toggleMicrophone = useCallback(() => {
    const newState = !isMicEnabled;
    console.log(`[Mic Toggle] Toggling microphone to ${newState ? 'ON' : 'OFF'}`);
    
    if (!newState) {
      // Turning microphone OFF - fully release resources
      console.log('[Mic Toggle] Stopping microphone and releasing resources');
      
      if (stream) {
        // First get all audio tracks
        const audioTracks = stream.getAudioTracks();
        
        // Store constraints before stopping
        if (audioTracks.length > 0) {
          try {
            const trackConstraints = audioTracks[0].getConstraints();
            setLastAudioConstraints(trackConstraints);
            console.log('[Mic Toggle] Saved audio constraints for future use');
          } catch (e) {
            console.warn('[Mic Toggle] Failed to save audio constraints:', e);
          }
        }
        
        // Disable and stop all audio tracks
        audioTracks.forEach(track => {
          try {
            // First disable the track
            track.enabled = false;
            
            // Capture track info before stopping
            const trackId = track.id;
            const trackLabel = track.label;
            
            // Then stop it
            track.stop();
            console.log(`[Mic Toggle] Successfully stopped audio track: ${trackId} (${trackLabel})`);
            
            // Clear the audio track reference
            if (audioTrackRef.current && audioTrackRef.current.id === trackId) {
              audioTrackRef.current = null;
            }
          } catch (e) {
            console.error('[Mic Toggle] Error stopping track:', e);
          }
        });
        
        // Keep only video tracks in the stream if camera is still enabled
        if (isCameraEnabled && stream.getVideoTracks().length > 0) {
          // IMPORTANT: Don't replace the stream, just remove audio tracks
          // This prevents disrupting the camera stream
          const videoTracks = stream.getVideoTracks();
          
          // Keep the original stream but remove all audio tracks
          audioTracks.forEach(track => {
            if (stream.getTracks().includes(track)) {
              stream.removeTrack(track);
            }
          });
          
          console.log(`[Mic Toggle] Removed audio tracks from stream. Stream now has ${stream.getAudioTracks().length} audio tracks and ${stream.getVideoTracks().length} video tracks`);
          
          // Only if absolutely needed (e.g., for Safari), create a new stream
          if (stream.getAudioTracks().length > 0) {
            console.log('[Mic Toggle] Audio tracks still present after removal, creating new stream as fallback');
            const videoOnlyStream = new MediaStream();
            videoTracks.forEach(videoTrack => {
              videoOnlyStream.addTrack(videoTrack);
            });
            
            // Update video element with video-only stream
            if (videoRef.current) {
              videoRef.current.srcObject = videoOnlyStream;
            }
            
            // Update stream reference
            setStream(videoOnlyStream);
          }
        } else if (!isCameraEnabled) {
          // No video tracks needed either, clear everything
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
          setStream(null);
        }
        
        // Force browsers to refresh device state
        setTimeout(() => {
          navigator.mediaDevices.enumerateDevices()
            .catch(err => console.warn('[Mic Toggle] Error refreshing device enumeration:', err));
        }, 100);
      }
      
      // Update UI state
      setIsMicEnabled(false);
    } else {
      // Turning microphone ON - first check permission status if possible
      console.log('[Mic Toggle] Turning microphone ON - checking permissions');
      
      // Function to request microphone access
      const requestMicrophoneAccess = () => {
        // Get constraints for audio, using saved constraints if available
        const constraints: MediaStreamConstraints = {
          audio: selectedAudioDevice 
            ? { deviceId: { exact: selectedAudioDevice } } 
            : (lastAudioConstraints || true),
        };
        
        // Request media with retry capability
        const attemptMediaRequest = (retryCount = 0) => {
          console.log(`[Mic Toggle] Requesting microphone access (attempt ${retryCount + 1})`);
          
          navigator.mediaDevices.getUserMedia(constraints)
            .then(newAudioStream => {
              // Get the new audio track
              const newAudioTrack = newAudioStream.getAudioTracks()[0];
              
              if (newAudioTrack) {
                // Store the audio track reference
                audioTrackRef.current = newAudioTrack;
                console.log(`[Mic Toggle] New audio track: ${audioTrackRef.current.id} (${audioTrackRef.current.label})`);
                
                if (stream) {
                  // First remove any existing audio tracks
                  stream.getAudioTracks().forEach(existingTrack => {
                    stream.removeTrack(existingTrack);
                  });
                  
                  // Add the new audio track to the existing stream
                  stream.addTrack(newAudioTrack);
                  
                  // Log the updated stream
                  console.log(`[Mic Toggle] Added new audio track to existing stream. Now has ${stream.getAudioTracks().length} audio tracks and ${stream.getVideoTracks().length} video tracks`);
                } else {
                  // No existing stream, create a new one
                  setStream(newAudioStream);
                  
                  // If we need to display video, update the video element
                  if (videoRef.current) {
                    videoRef.current.srcObject = newAudioStream;
                  }
                  
                  console.log(`[Mic Toggle] Created new stream with audio track`);
                }
                
                // Update permission state
                setHasMicPermission(true);
              }
              
              // Update UI state
              setIsMicEnabled(true);
            })
            .catch(error => {
              console.error('[Mic Toggle] Error accessing microphone:', error);
              
              if (error.name === 'NotAllowedError') {
                showToast('Microphone permission denied. Please enable it in browser settings manually.', 'error');
                return;
              }
              
              // Retry logic for transient errors (up to 2 retries)
              if (retryCount < 2 && 
                  (error.name === 'NotReadableError' || error.name === 'AbortError')) {
                console.log(`[Mic Toggle] Retrying microphone access after error: ${error.name}`);
                setTimeout(() => attemptMediaRequest(retryCount + 1), 500);
                return;
              }
              
              // Keep the UI state in sync with reality
              setIsMicEnabled(false);
              
              // If this was a permission denial, update the permission state
              if (error.name === 'NotAllowedError') {
                setHasMicPermission(false);
              }
            });
        };
        
        // Begin the request process
        attemptMediaRequest();
      };
      
      // Check if we need to request permission
      if (hasMicPermission === false) {
        // Permission was explicitly denied before, show a message to the user
        console.log('[Mic Toggle] Microphone permission was previously denied. User needs to enable it in browser settings.');
        
        // We'll still try to request it in case the user changed settings
        requestMicrophoneAccess();
      } else if (hasMicPermission === null) {
        // We don't know the permission status yet, so request it
        console.log('[Mic Toggle] Microphone permission status unknown, requesting permission');
        requestMicrophoneAccess();
      } else {
        // We already have permission, proceed with enabling
        console.log('[Mic Toggle] Microphone permission already granted, enabling microphone');
        requestMicrophoneAccess();
      }
    }
  }, [stream, videoRef, isMicEnabled, isCameraEnabled, selectedAudioDevice, lastAudioConstraints, hasMicPermission]);

  // Set up device change listener with debouncing
  useEffect(() => {
    const mountId = Date.now().toString().slice(-6);
    console.log(`[${mountId}] Setting up device change listener`);
    
    // Track error state to prevent continuous error loops
    let hasReportedError = false;
    
    // Use a ref to debounce rapid device change events
    let deviceChangeDebounceTimer: NodeJS.Timeout | null = null;
    
    // Initial device list - only attempt if we haven't already detected an error
    const initialDeviceCheck = async () => {
      try {
        await getMediaDevices();
        // If successful, reset error flag
        hasReportedError = false;
      } catch (error) {
        // If we get an error here, set the flag to prevent repeated errors
        hasReportedError = true;
        console.log(`[${mountId}] Initial device check failed, will not continuously poll`);
      }
    };
    
    // Execute the initial device check
    initialDeviceCheck();
    
    // Event listener for device changes with debouncing
    const handleDeviceChanges = () => {
      // Clear any existing timer
      if (deviceChangeDebounceTimer) {
        clearTimeout(deviceChangeDebounceTimer);
      }
      
      // Set a new timer to debounce rapid changes
      deviceChangeDebounceTimer = setTimeout(() => {
        console.log(`[${mountId}] Device change detected (debounced)`);
        
        // Only attempt to enumerate devices if we haven't already seen an error
        if (!hasReportedError) {
          getMediaDevices().catch(error => {
            console.log(`[${mountId}] Device change listener triggered error, pausing listener`, error);
            hasReportedError = true;
          });
        } else {
          console.log(`[${mountId}] Device change detected but skipping due to previous errors`);
        }
      }, 1000); // 1 second debounce
    };
    
    // Add the listener
    try {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChanges);
    } catch (error) {
      console.error(`[${mountId}] Error setting up device change listener:`, error);
    }
    
    // Cleanup
    return () => {
      console.log(`[${mountId}] Removing device change listener`);
      
      // Clear any pending debounce timer
      if (deviceChangeDebounceTimer) {
        clearTimeout(deviceChangeDebounceTimer);
      }
      
      // Remove the event listener
      try {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChanges);
      } catch (error) {
        console.error(`[${mountId}] Error removing device change listener:`, error);
      }
    };
  }, [getMediaDevices]);

  // Add document visibility change listener for better resource management
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Clean up resources when the tab becomes hidden
      if (document.visibilityState === 'hidden') {
        console.log('Tab visibility changed to hidden, cleaning up resources');
        
        // If camera or mic are disabled but resources might still be allocated, clean them up
        if (!isCameraEnabled || !isMicEnabled) {
          cleanupMediaTracks();
        }
      } else if (document.visibilityState === 'visible') {
        // When tab becomes visible again, check if we need to re-request media access
        console.log('Tab visibility changed to visible, checking media status');
        
        // Only re-request if camera or mic should be enabled
        if ((isCameraEnabled || isMicEnabled) && (!stream || !stream.active)) {
          console.log('Re-requesting media access after visibility change');
          requestMediaAccess();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [cleanupMediaTracks, requestMediaAccess, stream, isCameraEnabled, isMicEnabled]);

  // Enhance the before unload handler with full resource cleanup
  useEffect(() => {
    // Page unload handler - cleaned up and only triggered for actual page navigation
    const handleBeforeUnload = () => {
      // Only log on actual unload, not component unmount
      if (document.visibilityState !== 'hidden') {
        console.log("Page unload detected - performing complete resource cleanup");
      }
      
      // Clear video source directly (immediate cleanup)
      if (videoRef.current) {
        try {
          videoRef.current.pause();
          videoRef.current.srcObject = null;
          videoRef.current.load();
        } catch (e) {
          // Ignore errors during unload
        }
      }
      
      // Stop all tracks immediately
      if (stream) {
        stream.getTracks().forEach(track => {
          try {
            track.enabled = false;
            track.stop();
          } catch (e) {
            // Ignore errors during unload
          }
        });
      }
      
      // Clear references
      videoTrackRef.current = null;
      audioTrackRef.current = null;
    };
    
    // Register only for the most reliable cleanup event
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Component unmount cleanup - less aggressive to avoid breaking re-renders
      // Just stop the tracks but don't mess with references or trigger full unload
      if (stream) {
        stream.getTracks().forEach(track => {
          try {
            // Only stop the track if it's not being used elsewhere
            track.stop();
          } catch (e) {
            // Ignore errors during unmount
          }
        });
      }
    };
  }, [stream]);

  // Update the useEffect for camera initialization to use the defined variables
  useEffect(() => {
    // Don't try to initialize again if we're already initialized
    if (isStreamInitialized) return;
    
    // Don't retry beyond max attempts
    if (mediaRetryAttempts >= MAX_RETRY_ATTEMPTS) {
      console.log('[InitCamera] Maximum retry attempts reached');
      
      // Check what devices are available
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const hasAudioDevices = devices.some(device => device.kind === 'audioinput');
          const hasVideoDevices = devices.some(device => device.kind === 'videoinput');
          
          if (!hasAudioDevices && !hasVideoDevices) {
            setDeviceError({
              hasError: true,
              message: 'No audio or video devices found. Please connect a microphone and try again.',
              isBlocking: true
            });
          } else if (!hasAudioDevices) {
            setDeviceError({
              hasError: true,
              message: 'No microphone found. A microphone is required for the interview.',
              isBlocking: true
            });
          } else {
            // Fallback error - something else went wrong
            setDeviceError({
              hasError: true,
              message: 'Failed to initialize media devices after multiple attempts.',
              isBlocking: true
            });
          }
        })
        .catch(error => {
          console.error('[InitCamera] Error checking devices:', error);
          setDeviceError({
            hasError: true,
            message: 'Could not detect available media devices. Please ensure you have a microphone connected.',
            isBlocking: true
          });
        });
      
      return;
    }
    
    console.log(`[InitCamera] Initializing camera (attempt ${mediaRetryAttempts + 1}/${MAX_RETRY_ATTEMPTS})`);
    
    const initCamera = async () => {
      try {
        // First clean up any existing media tracks
        await cleanupMediaTracks();
        
        // Request camera and microphone access with the safe wrapper
        await safelyInitializeMedia({
          enableCamera: isCameraEnabled,
          enableMic: isMicEnabled
        });
        
        // If successful, mark as initialized
        setIsStreamInitialized(true);
        
        // Clear any device errors
        setDeviceError({
          hasError: false,
          message: '',
          isBlocking: false
        });
        
        // The stream is handled by the hooks and CameraPreview component
        console.log('[InitCamera] Camera initialized successfully');
      } catch (error) {
        console.error('[InitCamera] Error initializing camera:', error);
        
        // Increment retry counter and try again with a delay
        setMediaRetryAttempts(prev => prev + 1);
      }
    };
    
    // Add a delay between retry attempts to prevent rapid WebRTC requests
    const retryDelay = mediaRetryAttempts > 0 ? 2000 : 0;
    const initTimer = setTimeout(initCamera, retryDelay);
    
    // Cleanup on unmount
    return () => {
      clearTimeout(initTimer);
      
      if (!isStreamInitialized) {
        console.log('[InitCamera] Component unmounted before initialization completed');
      }
    };
  }, [cleanupMediaTracks, safelyInitializeMedia, isStreamInitialized, mediaRetryAttempts, isCameraEnabled, isMicEnabled]);

  return {
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    setSelectedVideoDevice,
    setSelectedAudioDevice,
    stream,
    videoRef,
    isCameraEnabled,
    isMicEnabled,
    hasCameraPermission,
    hasMicPermission,
    lastAudioConstraints,
    toggleCamera,
    toggleMicrophone,
    handleDeviceChange,
    cleanupMediaTracks,
    requestMediaAccess
  };
}; 